/**
 * This utility file provides the following methods, used by each submission service
 * 	- retry a single request by planId
 * 	- remove a request by planId
 * 	- retry all requests in a given queue, then prune all complete requests
 * 	- get all failed submissions in a queue
 * 	- get status when given a specific planId
 */
import { useRequestQueue } from '@/mixins/requestQueue'
import type { UploadItem } from '@/types'
import { captureException } from '@sentry/vue'

export const useRetryMethods = (
	queue: ReturnType<typeof useRequestQueue>,
	storage: LocalForage,
	serviceName: string
) => {
	// This method will retry all submissions that aren't complete from the submission IDB.
	const retryAllRequests = async (): Promise<void> => {
		await queue.retryAll().catch((error) => {
			const errorStatus = error.response?.status

			if (errorStatus === 403) {
				captureException(error, {
					extra: {
						message: 'AFP does not have correct capabilities.',
					},
				})
			} else {
				captureException(error, {
					extra: {
						message: `Error retrying failed submissions for ${serviceName}.`,
					},
				})
			}
		})
	}

	const removeRequestById = async (planHolderId: string): Promise<void> => {
		await storage.removeItem(planHolderId)
	}

	const getAllPlanIdInQueue = async (): Promise<string[]> => {
		return await storage.keys()
	}

	// This method retries a single request in one queue given a planHolderID
	const retryRequestById = async (planHolderId: string) => {
		// if planHolder does not have a request in this queue, simply return
		const request = await storage.getItem(planHolderId)
		if (!request) {
			return
		}
		await queue
			.retry(planHolderId)
			.then(async (result) => {})
			.catch((error) => {
				const errorStatus = error.response?.status

				if (errorStatus === 403) {
					captureException(error, {
						extra: {
							message: `AFP does not have correct capabilities to submit ${serviceName} packet.`,
						},
					})
				} else {
					captureException(error, {
						extra: {
							message: `Error submitting ${serviceName} packet for ${planHolderId}`,
						},
					})
				}
			})
	}

	// This method gathers the requests in a given service that needs to be retried
	const getFailedRequests = async () => {
		let failedSubmissions: any[] = []
		await storage.iterate((value: UploadItem<any>, key) => {
			if (queue.statusesToRetry.includes(value.uploadStatus)) {
				failedSubmissions.push({
					planHolderId: key,
					status: value.uploadStatus,
				})
			}
		})
		return failedSubmissions
	}

	const getSubmissionRequestById = async (
		planHolderId: string
	): Promise<UploadItem<any> | null> => {
		return await storage.getItem<UploadItem<any>>(planHolderId)
	}

	return {
		getAllPlanIdInQueue,
		removeRequestById,
		retryAllRequests,
		retryRequestById,
		getFailedRequests,
		getSubmissionRequestById,
	}
}
